/**
 * Runtime theme mode normalization — keep in sync with scripts/theme-mode-normalize.mjs
 */

export type ThemeModeColors = Record<string, string | undefined>

const PRIMARY_HOVER_FALLBACK = '#FF5722'

const EQUIVALENCE_GROUPS: string[][] = [
  ['colourSurface', 'background', 'bgPrimary'],
  ['colourSurfaceHeader', 'footerBg', 'lotteryCardGradient', 'bgDarkCard', 'bgDark'],
  [
    'colourSurfaceContLowest',
    'bgSecondary',
    'bgCard',
    'surface',
    'bgLeftSection',
    'backgroundSecondary',
  ],
  ['colourSurfaceContLow', 'bgRightSection', 'bgDarkTertiary', 'footerBg2', 'appHeaderBg'],
  ['colourSurfaceCont', 'bgTertiary', 'borderColor', 'border'],
  ['colourSurfaceContHigh', 'borderDark'],
  ['colourContHighestHigh', 'colourSurfaceContHighestHigh'],
  ['colourBaseTextColour', 'textPrimary', 'text', 'textOnControl'],
  ['colourSecondaryTextColour', 'textSecondary', 'textTertiary'],
  ['colourSecondaryTextOnDark', 'textResultInactive', 'textLight'],
  ['colourSecondaryIconColour', 'colourSecondaryTextColour', 'textSecondary'],
  ['colourIcons', 'colourBaseTextColour', 'textPrimary'],
  ['colourTextInverse', 'color', 'textWhite', 'textInverse'],
  ['colourOrangeButton', 'primary', 'accentColor', 'bgButton', 'bgPrimaryButton'],
  [
    'colourOrangeTextColour',
    'colourOrangeButton',
    'primary',
    'link',
    'appHeaderActiveLinkColor',
    'accentColor',
  ],
  ['colourOrangeIcons', 'colourOrangeTextColour', 'colourOrangeButton'],
  ['primaryHover', 'bgButtonHover', 'bgPrimaryButtonHover'],
  ['colourGreenButton', 'bgSuccess', 'success', 'colourGreenTextColour'],
  ['colourGreenTextColour', 'colourGreenButton', 'success'],
  ['colourGreenIcons', 'colourGreenTextColour', 'colourGreenButton'],
  ['colourYellowTextColour', 'bgWarning', 'warning'],
  ['colourYellowIcons', 'colourYellowTextColour'],
  ['colourRedTextColour', 'bgDanger', 'error', 'errorColor'],
  ['colourRedIcon', 'colourRedTextColour'],
  ['bgSecondaryButtonBorderColor', 'bgSecondaryBorder'],
]

function coalesce(mode: ThemeModeColors, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = mode[key]
    if (value != null && value !== '') return value
  }
  return undefined
}

export function normalizeThemeModeColors(raw: ThemeModeColors = {}): ThemeModeColors {
  const mode: ThemeModeColors = { ...raw }

  for (const group of EQUIVALENCE_GROUPS) {
    const value = coalesce(mode, group)
    if (value == null) continue
    for (const key of group) {
      if (mode[key] == null || mode[key] === '') {
        mode[key] = value
      }
    }
  }

  if (mode.primaryHover == null || mode.primaryHover === '') {
    mode.primaryHover = PRIMARY_HOVER_FALLBACK
  }
  if (mode.bgButtonHover == null || mode.bgButtonHover === '') {
    mode.bgButtonHover = mode.primaryHover
  }
  if (mode.bgPrimaryButtonHover == null || mode.bgPrimaryButtonHover === '') {
    mode.bgPrimaryButtonHover = mode.primaryHover
  }

  if (mode.success == null && mode.colourGreenTextColour) mode.success = mode.colourGreenTextColour
  if (mode.warning == null && mode.colourYellowTextColour) mode.warning = mode.colourYellowTextColour
  if (mode.error == null && mode.colourRedTextColour) mode.error = mode.colourRedTextColour
  if (mode.link == null && mode.colourOrangeTextColour) mode.link = mode.colourOrangeTextColour
  if (mode.accentColor == null && mode.colourOrangeButton) mode.accentColor = mode.colourOrangeButton

  return mode
}
